import { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  Download,
  Upload,
  Trash2,
  Chrome,
  Smartphone,
  Apple,
  Package,
  FileText,
  Calendar,
  User,
  HardDrive,
  X,
  Loader2,
  AlertCircle,
  Check,
  Globe,
  Compass,
  ExternalLink,
  Edit2,
  Copy,
  CheckCircle,
  Key,
  Plus,
  Clock,
  Shield,
  HelpCircle,
  ChevronDown,
  ChevronUp,
  Monitor,
} from 'lucide-react';
import { api, type DevBuild, type DevBuildApiKeyCreated } from '../../services/api';
import { useRole, ROLES } from '../../hooks/usePermission';
import QRCode from 'qrcode';
import { extractVersionFromFile } from '../../utils/version-extractor';

type BuildType = 'extension-chrome' | 'extension-firefox' | 'extension-safari' | 'android' | 'ios';

const BUILD_TYPE_CONFIG: Record<
  BuildType,
  {
    label: string;
    shortLabel: string;
    icon: typeof Chrome;
    accept: string;
    color: string;
    disabled?: boolean;
    description?: string;
    isTestFlight?: boolean;
  }
> = {
  'extension-chrome': {
    label: 'Chrome Extension',
    shortLabel: 'Chrome',
    icon: Chrome,
    accept: '.zip',
    color: 'bg-blue-100 text-blue-700',
    description: 'Also works with Edge, Brave, Opera',
  },
  'extension-firefox': {
    label: 'Firefox Extension',
    shortLabel: 'Firefox',
    icon: Globe, // Using Globe as Firefox placeholder
    accept: '.zip',
    color: 'bg-orange-100 text-orange-700',
    description: 'Firefox Add-on (.xpi)',
  },
  'extension-safari': {
    label: 'Safari Extension',
    shortLabel: 'Safari',
    icon: Compass,
    accept: '.zip',
    color: 'bg-sky-100 text-sky-700',
    description: 'Requires macOS app wrapper',
  },
  android: {
    label: 'Android APK',
    shortLabel: 'Android',
    icon: Smartphone,
    accept: '.apk',
    color: 'bg-green-100 text-green-700',
  },
  ios: {
    label: 'iOS App',
    shortLabel: 'iOS',
    icon: Apple,
    accept: '.ipa',
    color: 'bg-slate-100 text-slate-700',
    isTestFlight: true,
    description: 'Join via TestFlight',
  },
};

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

// First Time Tester Guide - collapsible instructions for all platforms
function FirstTimeTesterGuide() {
  const [isExpanded, setIsExpanded] = useState(false);
  const [activeGuide, setActiveGuide] = useState<'android' | 'ios' | 'extensions'>('android');

  return (
    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200 overflow-hidden mb-6">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full p-4 flex items-center justify-between text-left hover:bg-blue-100/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-100 rounded-lg">
            <HelpCircle className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">First Time Tester?</h3>
            <p className="text-sm text-gray-600">Click to see installation instructions for all platforms</p>
          </div>
        </div>
        {isExpanded ? (
          <ChevronUp className="w-5 h-5 text-gray-500" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-500" />
        )}
      </button>

      {isExpanded && (
        <div className="border-t border-blue-200">
          {/* Platform Tabs */}
          <div className="flex border-b border-blue-200 bg-white/50">
            <button
              onClick={() => setActiveGuide('android')}
              className={`flex-1 px-4 py-3 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
                activeGuide === 'android'
                  ? 'text-green-700 border-b-2 border-green-600 bg-green-50'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <Smartphone className="w-4 h-4" />
              Android APK
            </button>
            <button
              onClick={() => setActiveGuide('ios')}
              className={`flex-1 px-4 py-3 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
                activeGuide === 'ios'
                  ? 'text-gray-700 border-b-2 border-gray-600 bg-gray-50'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <Apple className="w-4 h-4" />
              iOS (TestFlight)
            </button>
            <button
              onClick={() => setActiveGuide('extensions')}
              className={`flex-1 px-4 py-3 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
                activeGuide === 'extensions'
                  ? 'text-blue-700 border-b-2 border-blue-600 bg-blue-50'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <Monitor className="w-4 h-4" />
              Browser Extensions
            </button>
          </div>

          {/* Android Instructions */}
          {activeGuide === 'android' && (
            <div className="p-6 space-y-4">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-green-100 rounded-lg flex-shrink-0">
                  <Smartphone className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Installing the Android APK</h4>
                  <p className="text-sm text-gray-600">Follow these steps to install RUMIO on your Android phone</p>
                </div>
              </div>

              <div className="space-y-4">
                {/* Step 1 */}
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center font-bold text-sm">1</div>
                  <div className="flex-1">
                    <h5 className="font-medium text-gray-900">Enable Unknown Sources</h5>
                    <p className="text-sm text-gray-600 mt-1">
                      On your Android phone, you need to allow installing apps from outside the Play Store:
                    </p>
                    <div className="mt-2 p-3 bg-white rounded-lg border border-gray-200 text-sm">
                      <p className="font-medium text-gray-700 mb-2">For most Android phones:</p>
                      <ol className="list-decimal list-inside text-gray-600 space-y-1">
                        <li>Open <strong>Settings</strong></li>
                        <li>Go to <strong>Security</strong> or <strong>Privacy</strong></li>
                        <li>Enable <strong>"Install unknown apps"</strong> or <strong>"Unknown sources"</strong></li>
                        <li>Select your browser (Chrome) and toggle <strong>Allow</strong></li>
                      </ol>
                      <p className="mt-2 text-xs text-gray-500">
                        <AlertCircle className="w-3 h-3 inline mr-1" />
                        On Samsung: Settings → Biometrics and security → Install unknown apps
                      </p>
                    </div>
                  </div>
                </div>

                {/* Step 2 */}
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center font-bold text-sm">2</div>
                  <div className="flex-1">
                    <h5 className="font-medium text-gray-900">Download the APK</h5>
                    <p className="text-sm text-gray-600 mt-1">
                      On your Android phone, open this page (scan the QR code below) and tap <strong>Download</strong> on the latest Android build.
                    </p>
                  </div>
                </div>

                {/* Step 3 */}
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center font-bold text-sm">3</div>
                  <div className="flex-1">
                    <h5 className="font-medium text-gray-900">Install the APK</h5>
                    <p className="text-sm text-gray-600 mt-1">
                      Once downloaded, tap the notification or open your <strong>Downloads</strong> folder and tap the APK file. Then tap <strong>Install</strong>.
                    </p>
                    <div className="mt-2 p-3 bg-amber-50 rounded-lg border border-amber-200 text-sm text-amber-800">
                      <AlertCircle className="w-4 h-4 inline mr-1" />
                      If you see "Blocked by Play Protect", tap <strong>More details</strong> → <strong>Install anyway</strong>
                    </div>
                  </div>
                </div>

                {/* Step 4 */}
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center font-bold text-sm">4</div>
                  <div className="flex-1">
                    <h5 className="font-medium text-gray-900">Open RUMIO</h5>
                    <p className="text-sm text-gray-600 mt-1">
                      Once installed, tap <strong>Open</strong> or find RUMIO in your app drawer. Sign in with your account to start testing!
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* iOS Instructions */}
          {activeGuide === 'ios' && (
            <div className="p-6 space-y-4">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-gray-100 rounded-lg flex-shrink-0">
                  <Apple className="w-5 h-5 text-gray-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Installing via TestFlight</h4>
                  <p className="text-sm text-gray-600">Apple requires beta apps to be installed through TestFlight</p>
                </div>
              </div>

              <div className="space-y-4">
                {/* Step 1 */}
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-gray-700 text-white rounded-full flex items-center justify-center font-bold text-sm">1</div>
                  <div className="flex-1">
                    <h5 className="font-medium text-gray-900">Install TestFlight</h5>
                    <p className="text-sm text-gray-600 mt-1">
                      Download <strong>TestFlight</strong> from the App Store if you don't have it already. It's Apple's official app for beta testing.
                    </p>
                    <a
                      href="https://apps.apple.com/app/testflight/id899247664"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 mt-2 px-3 py-2 bg-black text-white rounded-lg text-sm hover:bg-gray-800 transition-colors"
                    >
                      <Apple className="w-4 h-4" />
                      Get TestFlight
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                </div>

                {/* Step 2 */}
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-gray-700 text-white rounded-full flex items-center justify-center font-bold text-sm">2</div>
                  <div className="flex-1">
                    <h5 className="font-medium text-gray-900">Open the TestFlight Invite</h5>
                    <p className="text-sm text-gray-600 mt-1">
                      Go to the <strong>iOS</strong> tab on this page and scan the QR code or tap the TestFlight link on your iPhone.
                    </p>
                  </div>
                </div>

                {/* Step 3 */}
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-gray-700 text-white rounded-full flex items-center justify-center font-bold text-sm">3</div>
                  <div className="flex-1">
                    <h5 className="font-medium text-gray-900">Accept the Invitation</h5>
                    <p className="text-sm text-gray-600 mt-1">
                      TestFlight will open automatically. Tap <strong>Accept</strong> to join the beta program.
                    </p>
                  </div>
                </div>

                {/* Step 4 */}
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-gray-700 text-white rounded-full flex items-center justify-center font-bold text-sm">4</div>
                  <div className="flex-1">
                    <h5 className="font-medium text-gray-900">Install RUMIO</h5>
                    <p className="text-sm text-gray-600 mt-1">
                      Tap <strong>Install</strong> next to RUMIO in TestFlight. The app will appear on your home screen when done.
                    </p>
                    <div className="mt-2 p-3 bg-blue-50 rounded-lg border border-blue-200 text-sm text-blue-800">
                      <Check className="w-4 h-4 inline mr-1" />
                      Future updates will be automatically available in TestFlight - you'll get a notification!
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Browser Extensions Instructions */}
          {activeGuide === 'extensions' && (
            <div className="p-6 space-y-4">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-blue-100 rounded-lg flex-shrink-0">
                  <Monitor className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Installing Browser Extensions</h4>
                  <p className="text-sm text-gray-600">Extensions are for desktop browsers (Chrome, Firefox, Safari on Mac)</p>
                </div>
              </div>

              <div className="p-3 bg-amber-50 rounded-lg border border-amber-200 text-sm text-amber-800 mb-4">
                <AlertCircle className="w-4 h-4 inline mr-1" />
                <strong>Note:</strong> Browser extensions work on <strong>desktop/laptop computers only</strong>. Mobile browsers (Chrome on Android, Safari on iPhone) do not support extensions.
              </div>

              {/* Chrome/Edge/Brave */}
              <div className="p-4 bg-white rounded-lg border border-gray-200">
                <h5 className="font-medium text-gray-900 flex items-center gap-2 mb-3">
                  <Chrome className="w-4 h-4 text-blue-500" />
                  Chrome, Edge, Brave, Opera
                </h5>
                <ol className="text-sm text-gray-600 space-y-2 list-decimal list-inside">
                  <li>Download the <strong>Chrome Extension</strong> (.zip file) from the Chrome tab</li>
                  <li>Extract the .zip file to a folder on your computer</li>
                  <li>Open your browser and go to the extensions page:
                    <ul className="ml-6 mt-1 text-xs text-gray-500">
                      <li>Chrome: <code className="bg-gray-100 px-1 rounded">chrome://extensions</code></li>
                      <li>Edge: <code className="bg-gray-100 px-1 rounded">edge://extensions</code></li>
                      <li>Brave: <code className="bg-gray-100 px-1 rounded">brave://extensions</code></li>
                    </ul>
                  </li>
                  <li>Enable <strong>"Developer mode"</strong> (toggle in top-right corner)</li>
                  <li>Click <strong>"Load unpacked"</strong> and select the extracted folder</li>
                  <li>The RUMIO extension should now appear in your toolbar!</li>
                </ol>
              </div>

              {/* Firefox */}
              <div className="p-4 bg-white rounded-lg border border-gray-200">
                <h5 className="font-medium text-gray-900 flex items-center gap-2 mb-3">
                  <Globe className="w-4 h-4 text-orange-500" />
                  Firefox
                </h5>
                <ol className="text-sm text-gray-600 space-y-2 list-decimal list-inside">
                  <li>Download the <strong>Firefox Extension</strong> (.zip file) from the Firefox tab</li>
                  <li>Open Firefox and go to <code className="bg-gray-100 px-1 rounded">about:debugging</code></li>
                  <li>Click <strong>"This Firefox"</strong> in the left sidebar</li>
                  <li>Click <strong>"Load Temporary Add-on"</strong></li>
                  <li>Select the .zip file (or any file inside the extracted folder)</li>
                </ol>
                <p className="text-xs text-gray-500 mt-2">
                  <AlertCircle className="w-3 h-3 inline mr-1" />
                  Note: Temporary add-ons are removed when Firefox closes. For permanent installation, the extension needs to be signed by Mozilla.
                </p>
              </div>

              {/* Safari */}
              <div className="p-4 bg-white rounded-lg border border-gray-200">
                <h5 className="font-medium text-gray-900 flex items-center gap-2 mb-3">
                  <Compass className="w-4 h-4 text-sky-500" />
                  Safari (macOS only)
                </h5>
                <ol className="text-sm text-gray-600 space-y-2 list-decimal list-inside">
                  <li>Download the <strong>Safari Extension</strong> from the Safari tab</li>
                  <li>The download includes a macOS app wrapper - open it</li>
                  <li>Go to <strong>Safari → Settings → Extensions</strong></li>
                  <li>Enable the <strong>RUMIO</strong> extension</li>
                  <li>Grant the required permissions when prompted</li>
                </ol>
                <p className="text-xs text-gray-500 mt-2">
                  <AlertCircle className="w-3 h-3 inline mr-1" />
                  Safari extensions require macOS and must be packaged as a macOS app.
                </p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Android QR code section component - shows QR to admin page (requires login)
function AndroidQRSection({ latestBuild }: { latestBuild?: DevBuild }) {
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // QR code links to admin dev builds page (secure - requires login)
  const pageUrl = 'https://rumio.world/admin/dev-builds';

  // Generate QR code
  useEffect(() => {
    if (latestBuild) {
      QRCode.toDataURL(pageUrl, {
        width: 200,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#ffffff',
        },
      }).then(setQrDataUrl).catch(console.error);
    } else {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Clear QR when no build
      setQrDataUrl(null);
    }
  }, [latestBuild]);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(pageUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!latestBuild) {
    return null;
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-6">
      <div className="p-6 border-b border-gray-100">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <Smartphone className="w-5 h-5" />
              Quick Install
              <span className="px-2 py-0.5 text-sm bg-green-100 text-green-700 rounded-full">
                v{latestBuild.version}
              </span>
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              Scan to open this page on your Android device
            </p>
          </div>
        </div>
      </div>

      <div className="p-6">
        <div className="flex flex-col md:flex-row items-center gap-8">
          {/* QR Code */}
          {qrDataUrl && (
            <div className="flex-shrink-0">
              <div className="bg-white p-4 rounded-xl border-2 border-gray-200 shadow-sm">
                <img src={qrDataUrl} alt="Dev Builds Page QR Code" className="w-48 h-48" />
              </div>
              <p className="text-xs text-gray-500 text-center mt-2">
                Scan with your phone camera
              </p>
            </div>
          )}

          {/* Instructions */}
          <div className="flex-1 text-center md:text-left">
            <div className="mb-4">
              <p className="text-sm font-medium text-gray-700 mb-2">Page Link</p>
              <div className="flex items-center gap-2 flex-wrap justify-center md:justify-start">
                <code className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded text-sm">
                  {pageUrl}
                </code>
                <button
                  onClick={handleCopy}
                  className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Copy link"
                >
                  {copied ? (
                    <CheckCircle className="w-5 h-5 text-green-500" />
                  ) : (
                    <Copy className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>

            <div className="bg-green-50 rounded-lg p-4">
              <h4 className="font-medium text-gray-900 mb-2">How to install:</h4>
              <ol className="text-sm text-gray-600 space-y-1 list-decimal list-inside">
                <li>Scan the QR code with your Android phone</li>
                <li>Log in with your admin account</li>
                <li>Tap Download on the latest build</li>
                <li>Open the APK and install</li>
              </ol>
            </div>

            <p className="text-xs text-gray-400 mt-3 flex items-center gap-1">
              <AlertCircle className="w-3 h-3" />
              Secure: Requires admin login to download
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// TestFlight section component for iOS
function TestFlightSection({ isSuperAdmin }: { isSuperAdmin: boolean }) {
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [editUrl, setEditUrl] = useState('');
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['testflight-url'],
    queryFn: () => api.getTestFlightUrl(),
  });

  const updateMutation = useMutation({
    mutationFn: (url: string) => api.setTestFlightUrl(url),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['testflight-url'] });
      setIsEditing(false);
    },
    onError: (error: Error) => {
      alert(`Failed to update: ${error.message}`);
    },
  });

  // Generate QR code when URL changes
  useEffect(() => {
    if (data?.url) {
      QRCode.toDataURL(data.url, {
        width: 200,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#ffffff',
        },
      }).then(setQrDataUrl).catch(console.error);
    } else {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Clear QR when no URL
      setQrDataUrl(null);
    }
  }, [data?.url]);

  const handleEdit = () => {
    setEditUrl(data?.url || '');
    setIsEditing(true);
  };

  const handleSave = () => {
    if (!editUrl.trim()) return;
    updateMutation.mutate(editUrl.trim());
  };

  const handleCopy = async () => {
    if (data?.url) {
      await navigator.clipboard.writeText(data.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
        <Loader2 className="w-8 h-8 mx-auto mb-4 animate-spin text-gray-400" />
        <p className="text-gray-500">Loading TestFlight configuration...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center text-red-500">
        <AlertCircle className="w-8 h-8 mx-auto mb-4" />
        <p>Failed to load TestFlight configuration</p>
      </div>
    );
  }

  // No URL configured yet
  if (!data?.url && !isEditing) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
        <Apple className="w-12 h-12 mx-auto mb-4 text-gray-300" />
        <p className="text-gray-500 mb-4">TestFlight link not configured yet</p>
        {isSuperAdmin && (
          <button
            onClick={handleEdit}
            className="text-primary-600 hover:text-primary-700 font-medium"
          >
            Configure TestFlight URL
          </button>
        )}
      </div>
    );
  }

  // Editing mode
  if (isEditing) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="font-semibold text-gray-900 mb-4">Configure TestFlight URL</h3>
        <div className="space-y-4">
          <div>
            <label htmlFor="testflight-url" className="block text-sm font-medium text-gray-700 mb-2">
              TestFlight Public Link
            </label>
            <input
              id="testflight-url"
              type="url"
              value={editUrl}
              onChange={(e) => setEditUrl(e.target.value)}
              placeholder="https://testflight.apple.com/join/XXXXXXXX"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
            <p className="mt-2 text-sm text-gray-500">
              Get this from App Store Connect → Your App → TestFlight → Enable Public Link
            </p>
          </div>
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setIsEditing(false)}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={updateMutation.isPending || !editUrl.trim()}
              className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
            >
              {updateMutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
              Save
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Display TestFlight link and QR code
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <div className="p-6 border-b border-gray-100">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <Apple className="w-5 h-5" />
              iOS TestFlight
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              Join the beta via Apple TestFlight
            </p>
          </div>
          {isSuperAdmin && (
            <button
              onClick={handleEdit}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              title="Edit TestFlight URL"
            >
              <Edit2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      <div className="p-6">
        <div className="flex flex-col md:flex-row items-center gap-8">
          {/* QR Code */}
          {qrDataUrl && (
            <div className="flex-shrink-0">
              <div className="bg-white p-4 rounded-xl border-2 border-gray-200 shadow-sm">
                <img src={qrDataUrl} alt="TestFlight QR Code" className="w-48 h-48" />
              </div>
              <p className="text-xs text-gray-500 text-center mt-2">
                Scan with your iPhone camera
              </p>
            </div>
          )}

          {/* Link and instructions */}
          <div className="flex-1 text-center md:text-left">
            <div className="mb-4">
              <p className="text-sm font-medium text-gray-700 mb-2">TestFlight Link</p>
              <div className="flex items-center gap-2 flex-wrap justify-center md:justify-start">
                <a
                  href={data?.url || '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors"
                >
                  <Apple className="w-4 h-4" />
                  Join on TestFlight
                  <ExternalLink className="w-4 h-4" />
                </a>
                <button
                  onClick={handleCopy}
                  className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Copy link"
                >
                  {copied ? (
                    <CheckCircle className="w-5 h-5 text-green-500" />
                  ) : (
                    <Copy className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>

            <div className="bg-slate-50 rounded-lg p-4">
              <h4 className="font-medium text-gray-900 mb-2">How to install:</h4>
              <ol className="text-sm text-gray-600 space-y-1 list-decimal list-inside">
                <li>Scan the QR code or tap the button above on your iPhone</li>
                <li>Install TestFlight from the App Store if you don't have it</li>
                <li>Accept the invitation to join the beta</li>
                <li>Install the RUMIO app from TestFlight</li>
              </ol>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// API Keys Section for automated build uploads
function ApiKeysSection() {
  const queryClient = useQueryClient();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [createdKey, setCreatedKey] = useState<DevBuildApiKeyCreated | null>(null);
  const [copiedKey, setCopiedKey] = useState(false);
  const [revokeConfirm, setRevokeConfirm] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['dev-build-api-keys'],
    queryFn: () => api.getDevBuildApiKeys(),
  });

  const createMutation = useMutation({
    mutationFn: (name: string) => api.createDevBuildApiKey(name),
    onSuccess: (key) => {
      setCreatedKey(key);
      setNewKeyName('');
      queryClient.invalidateQueries({ queryKey: ['dev-build-api-keys'] });
    },
    onError: (error: Error) => {
      alert(`Failed to create API key: ${error.message}`);
    },
  });

  const revokeMutation = useMutation({
    mutationFn: (keyId: string) => api.revokeDevBuildApiKey(keyId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dev-build-api-keys'] });
      setRevokeConfirm(null);
    },
    onError: (error: Error) => {
      alert(`Failed to revoke API key: ${error.message}`);
    },
  });

  const handleCopyKey = async () => {
    if (createdKey?.key) {
      await navigator.clipboard.writeText(createdKey.key);
      setCopiedKey(true);
      setTimeout(() => setCopiedKey(false), 2000);
    }
  };

  const handleCreate = () => {
    if (newKeyName.trim()) {
      createMutation.mutate(newKeyName.trim());
    }
  };

  const activeKeys = data?.keys?.filter(k => k.is_active) || [];

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <div className="p-6 border-b border-gray-100">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <Key className="w-5 h-5" />
              API Keys
              <span className="px-2 py-0.5 text-xs bg-purple-100 text-purple-700 rounded-full">
                For Automation
              </span>
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              Create permanent API keys for automated build uploads
            </p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-3 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm"
          >
            <Plus className="w-4 h-4" />
            Create Key
          </button>
        </div>
      </div>

      <div className="p-6">
        {isLoading ? (
          <div className="text-center py-8">
            <Loader2 className="w-8 h-8 mx-auto mb-4 animate-spin text-gray-400" />
            <p className="text-gray-500">Loading API keys...</p>
          </div>
        ) : error ? (
          <div className="text-center py-8 text-red-500">
            <AlertCircle className="w-8 h-8 mx-auto mb-4" />
            <p>Failed to load API keys</p>
          </div>
        ) : activeKeys.length === 0 ? (
          <div className="text-center py-8">
            <Key className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p className="text-gray-500 mb-2">No API keys yet</p>
            <p className="text-sm text-gray-400">
              Create an API key to enable automated build uploads via CLI
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {activeKeys.map((key) => (
              <div
                key={key.id}
                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <span className="font-medium text-gray-900">{key.name}</span>
                    <code className="px-2 py-0.5 bg-gray-200 text-gray-600 rounded text-xs font-mono">
                      {key.key_prefix}
                    </code>
                  </div>
                  <div className="flex gap-4 mt-1 text-xs text-gray-500">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      Created {new Date(key.created_at).toLocaleDateString()}
                    </span>
                    {key.last_used_at && (
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        Last used {new Date(key.last_used_at).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => setRevokeConfirm(key.id)}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  title="Revoke key"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Usage instructions */}
        <div className="mt-6 p-4 bg-slate-50 rounded-lg">
          <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
            <Shield className="w-4 h-4" />
            How to use API keys
          </h4>
          <ol className="text-sm text-gray-600 space-y-1 list-decimal list-inside">
            <li>Create an API key above</li>
            <li>Add it to your <code className="bg-gray-200 px-1 rounded">.env.local</code> file:</li>
          </ol>
          <pre className="mt-2 p-3 bg-gray-800 text-green-400 rounded text-xs overflow-x-auto">
{`DEV_BUILDS_API_KEY=rumio_bk_...`}
          </pre>
          <p className="text-xs text-gray-500 mt-2">
            Then run <code className="bg-gray-200 px-1 rounded">npm run build:all:upload</code> to build and upload all platforms.
          </p>
        </div>
      </div>

      {/* Create Key Modal */}
      {showCreateModal && !createdKey && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Key className="w-5 h-5" />
              Create API Key
            </h3>
            <div className="space-y-4">
              <div>
                <label htmlFor="api-key-name" className="block text-sm font-medium text-gray-700 mb-2">
                  Key Name
                </label>
                <input
                  id="api-key-name"
                  type="text"
                  value={newKeyName}
                  onChange={(e) => setNewKeyName(e.target.value)}
                  placeholder="e.g., Build Server, CI/CD, Local Dev"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  autoFocus
                />
                <p className="text-xs text-gray-500 mt-1">
                  Give it a name to identify where it's used
                </p>
              </div>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    setNewKeyName('');
                  }}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreate}
                  disabled={createMutation.isPending || !newKeyName.trim()}
                  className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
                >
                  {createMutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
                  Create Key
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Key Created Modal - Show the key only once! */}
      {createdKey && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">API Key Created!</h3>
              <p className="text-sm text-gray-500 mt-1">
                Copy and save this key now - you won't be able to see it again!
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <span className="block text-sm font-medium text-gray-700 mb-2">
                  Your API Key
                </span>
                <div className="flex items-center gap-2">
                  <code className="flex-1 p-3 bg-gray-100 rounded-lg text-sm font-mono break-all">
                    {createdKey.key}
                  </code>
                  <button
                    onClick={handleCopyKey}
                    className="p-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                    title="Copy key"
                  >
                    {copiedKey ? (
                      <CheckCircle className="w-5 h-5" />
                    ) : (
                      <Copy className="w-5 h-5" />
                    )}
                  </button>
                </div>
                {copiedKey && (
                  <p className="text-sm text-green-600 mt-2 flex items-center gap-1">
                    <Check className="w-4 h-4" />
                    Copied to clipboard!
                  </p>
                )}
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-sm text-amber-800 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  <span>
                    <strong>Important:</strong> This key will only be shown once.
                    Save it in a secure location like your <code className="bg-amber-100 px-1 rounded">.env.local</code> file.
                  </span>
                </p>
              </div>

              <button
                onClick={() => {
                  setCreatedKey(null);
                  setShowCreateModal(false);
                }}
                className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                I've saved my key
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Revoke Confirmation Modal */}
      {revokeConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Revoke API Key</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to revoke this API key? Any scripts using this key will
              stop working immediately.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setRevokeConfirm(null)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => revokeMutation.mutate(revokeConfirm)}
                disabled={revokeMutation.isPending}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {revokeMutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
                Revoke Key
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// iOS Build Instructions Section
function IOSBuildInstructionsSection() {
  const [copiedCommand, setCopiedCommand] = useState<string | null>(null);

  const handleCopy = async (command: string) => {
    await navigator.clipboard.writeText(command);
    setCopiedCommand(command);
    setTimeout(() => setCopiedCommand(null), 2000);
  };

  const commands = [
    {
      command: 'npm run build:all:ios:upload',
      description: 'Build all platforms + iOS and upload (~25 min)',
    },
  ];

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mt-6">
      <div className="p-6 border-b border-gray-100">
        <div>
          <h3 className="font-semibold text-gray-900 flex items-center gap-2">
            <Apple className="w-5 h-5" />
            iOS Build Instructions
            <span className="px-2 py-0.5 text-xs bg-slate-100 text-slate-700 rounded-full">
              Super Admin
            </span>
          </h3>
          <p className="text-sm text-gray-500 mt-1">
            iOS is fully integrated into the build system. Builds run in EAS cloud, then auto-open App Store Connect.
          </p>
        </div>
      </div>

      <div className="p-6 space-y-6">
        {/* Step 1: Build Commands */}
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
            <span className="w-6 h-6 bg-primary-100 text-primary-700 rounded-full flex items-center justify-center text-xs font-bold">1</span>
            Run Build Command (PowerShell)
          </h4>
          <div className="space-y-2">
            {commands.map((item) => (
              <div
                key={item.command}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg group hover:bg-gray-100 transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <code className="text-sm font-mono text-gray-800 break-all">{item.command}</code>
                  <p className="text-xs text-gray-500 mt-0.5">{item.description}</p>
                </div>
                <button
                  onClick={() => handleCopy(item.command)}
                  className="ml-3 p-2 text-gray-400 hover:text-gray-600 hover:bg-white rounded-lg transition-colors flex-shrink-0"
                  title="Copy command"
                >
                  {copiedCommand === item.command ? (
                    <CheckCircle className="w-4 h-4 text-green-500" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </button>
              </div>
            ))}
          </div>
          <p className="text-xs text-gray-500 mt-3">
            The script will build in EAS cloud, submit to App Store Connect, and automatically open your browser.
          </p>
        </div>

        {/* Step 2: Export Compliance */}
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
            <span className="w-6 h-6 bg-primary-100 text-primary-700 rounded-full flex items-center justify-center text-xs font-bold">2</span>
            Answer Export Compliance (Browser)
          </h4>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <p className="text-sm text-amber-800 mb-2">
              <strong>One-click manual step:</strong> When App Store Connect opens:
            </p>
            <ol className="text-sm text-amber-700 space-y-2 list-decimal list-inside">
              <li>Go to <strong>TestFlight</strong> tab (should already be there)</li>
              <li>Find your build showing <strong>"Waiting for Review"</strong> (yellow dot)</li>
              <li><strong>Click on that build row</strong> to open the detail panel</li>
              <li>Look for <strong>"Missing Compliance"</strong> or <strong>"Manage"</strong> link</li>
              <li>Answer "Does your app use encryption?" → Select <strong>"No"</strong></li>
            </ol>
            <p className="text-xs text-amber-600 mt-2 italic">
              Note: The compliance question appears inside the build details, not on the main list.
            </p>
          </div>
        </div>

        {/* Step 3: Done */}
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
            <span className="w-6 h-6 bg-green-100 text-green-700 rounded-full flex items-center justify-center text-xs font-bold">✓</span>
            Done!
          </h4>
          <p className="text-sm text-gray-600 bg-green-50 rounded-lg p-4">
            Testers are notified automatically in the TestFlight app.
          </p>
        </div>

        {/* Notes */}
        <div className="border-t border-gray-200 pt-4">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Notes</h4>
          <ul className="text-xs text-gray-500 space-y-1 list-disc list-inside">
            <li>EAS builds run in Expo's cloud - works from Windows, no macOS required</li>
            <li>Build time is typically 15-20 minutes</li>
            <li>You can monitor progress at{' '}
              <a href="https://expo.dev" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">
                expo.dev
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}

// CLI Commands Section with copy functionality
function CLICommandsSection() {
  const [copiedCommand, setCopiedCommand] = useState<string | null>(null);

  const handleCopy = async (command: string) => {
    await navigator.clipboard.writeText(command);
    setCopiedCommand(command);
    setTimeout(() => setCopiedCommand(null), 2000);
  };

  const commands = [
    {
      category: 'Build Commands',
      items: [
        {
          command: 'npm run build:all:ios:upload',
          description: 'Build all platforms + iOS and upload (~25 min)',
        },
      ],
    },
    {
      category: 'Version Management',
      items: [
        {
          command: 'npm run version:get',
          description: 'Show current version',
        },
        {
          command: 'npm run version:bump:patch',
          description: '1.0.0 → 1.0.1 (bug fixes)',
        },
        {
          command: 'npm run version:bump:minor',
          description: '1.0.0 → 1.1.0 (new features)',
        },
        {
          command: 'npm run version:bump:major',
          description: '1.0.0 → 2.0.0 (breaking changes)',
        },
      ],
    },
  ];

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <div className="p-6 border-b border-gray-100">
        <div>
          <h3 className="font-semibold text-gray-900 flex items-center gap-2">
            <Package className="w-5 h-5" />
            CLI Commands
          </h3>
          <p className="text-sm text-gray-500 mt-1">
            Run these commands from the project root directory
          </p>
        </div>
      </div>

      <div className="p-6 space-y-6">
        {commands.map((section) => (
          <div key={section.category}>
            <h4 className="text-sm font-medium text-gray-700 mb-3">{section.category}</h4>
            <div className="space-y-2">
              {section.items.map((item) => (
                <div
                  key={item.command}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg group hover:bg-gray-100 transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <code className="text-sm font-mono text-gray-800">{item.command}</code>
                    <p className="text-xs text-gray-500 mt-0.5">{item.description}</p>
                  </div>
                  <button
                    onClick={() => handleCopy(item.command)}
                    className="ml-3 p-2 text-gray-400 hover:text-gray-600 hover:bg-white rounded-lg transition-colors flex-shrink-0"
                    title="Copy command"
                  >
                    {copiedCommand === item.command ? (
                      <CheckCircle className="w-4 h-4 text-green-500" />
                    ) : (
                      <Copy className="w-4 h-4" />
                    )}
                  </button>
                </div>
              ))}
            </div>
          </div>
        ))}

        {/* Quick start instructions */}
        <div className="mt-4 p-4 bg-blue-50 rounded-lg">
          <h4 className="font-medium text-blue-900 mb-2">Quick Start</h4>
          <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
            <li>Create an API key in the section above</li>
            <li>Add it to <code className="bg-blue-100 px-1 rounded">.env.local</code> in the project root</li>
            <li>Run <code className="bg-blue-100 px-1 rounded">npm run build:all:upload</code> to build and deploy</li>
          </ol>
        </div>
      </div>
    </div>
  );
}

export default function DevBuilds() {
  const { t } = useLanguage();
  const role = useRole();
  const isSuperAdmin = role === ROLES.SUPER_ADMIN;
  const queryClient = useQueryClient();

  const [activeTab, setActiveTab] = useState<BuildType>('extension-chrome');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-dev-builds'],
    queryFn: () => api.getDevBuilds(),
  });

  // Detect mobile browser for download method selection
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  );

  const downloadMutation = useMutation({
    mutationFn: async (buildId: string) => {
      if (isMobile) {
        // On mobile, use signed URL for direct download (blob approach fails on mobile browsers)
        const result = await api.getDevBuildDownloadUrl(buildId);
        // Redirect to the signed URL - browser will handle the download
        window.location.href = result.url;
        return null;
      } else {
        // On desktop, use blob approach
        return api.downloadDevBuild(buildId);
      }
    },
    onSuccess: (result) => {
      if (result) {
        // Desktop: trigger download from blob
        const url = URL.createObjectURL(result.blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = result.filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
      // Mobile: download already triggered via redirect
    },
    onError: (error: Error) => {
      alert(`Download failed: ${error.message}`);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (buildId: string) => api.deleteDevBuild(buildId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-dev-builds'] });
      setDeleteConfirm(null);
    },
    onError: (error: Error) => {
      alert(`Delete failed: ${error.message}`);
    },
  });

  // Map new build types to API response structure
  // The API returns builds grouped by type, we need to handle both old and new naming
  const builds = data?.builds as
    | {
        extension?: DevBuild[];
        'extension-chrome'?: DevBuild[];
        'extension-firefox'?: DevBuild[];
        'extension-safari'?: DevBuild[];
        android: DevBuild[];
        ios: DevBuild[]
      }
    | undefined;

  // Support both old 'extension' key and new 'extension-chrome' key for backwards compatibility
  const currentBuilds = (() => {
    if (!builds) return [];
    if (activeTab === 'extension-chrome') {
      return builds['extension-chrome'] || builds.extension || [];
    }
    return builds[activeTab] || [];
  })();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Developer Builds</h1>
          <p className="text-sm text-gray-500 mt-1">
            Download pre-release builds for testing
          </p>
        </div>

        {/* Upload Button (Super Admin Only, not for iOS - uses TestFlight) */}
        {isSuperAdmin && activeTab !== 'ios' && (
          <button
            onClick={() => setShowUploadModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <Upload className="w-4 h-4" />
            Upload Build
          </button>
        )}
      </div>

      {/* First Time Tester Guide */}
      <FirstTimeTesterGuide />

      {/* Tab Navigation - Group extensions together */}
      <div className="space-y-3">
        {/* Browser Extensions */}
        <div>
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Browser Extensions</p>
          <div className="flex flex-wrap gap-2">
            {(['extension-chrome', 'extension-firefox', 'extension-safari'] as BuildType[]).map((type) => {
              const config = BUILD_TYPE_CONFIG[type];
              return (
                <button
                  key={type}
                  onClick={() => !config.disabled && setActiveTab(type)}
                  disabled={config.disabled}
                  title={config.description}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                    activeTab === type
                      ? 'bg-primary-600 text-white'
                      : config.disabled
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <config.icon className="w-4 h-4" />
                  {config.shortLabel}
                </button>
              );
            })}
          </div>
        </div>

        {/* Mobile Apps */}
        <div>
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Mobile Apps</p>
          <div className="flex flex-wrap gap-2">
            {(['android', 'ios'] as BuildType[]).map((type) => {
              const config = BUILD_TYPE_CONFIG[type];
              return (
                <button
                  key={type}
                  onClick={() => !config.disabled && setActiveTab(type)}
                  disabled={config.disabled}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                    activeTab === type
                      ? 'bg-primary-600 text-white'
                      : config.disabled
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <config.icon className="w-4 h-4" />
                  {config.shortLabel}
                  {config.disabled && (
                    <span className="text-xs bg-gray-200 text-gray-500 px-1.5 py-0.5 rounded">
                      Soon
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Browser compatibility note */}
      {activeTab === 'extension-chrome' && (
        <p className="text-sm text-gray-500 bg-blue-50 px-4 py-2 rounded-lg">
          <Chrome className="w-4 h-4 inline-block mr-1" />
          Chrome extension also works with <strong>Microsoft Edge</strong>, <strong>Brave</strong>, and <strong>Opera</strong>.
        </p>
      )}
      {activeTab === 'extension-safari' && (
        <p className="text-sm text-gray-500 bg-sky-50 px-4 py-2 rounded-lg">
          <Compass className="w-4 h-4 inline-block mr-1" />
          Safari extension requires opening the downloaded app on macOS. Enable it in Safari → Settings → Extensions.
        </p>
      )}

      {/* Android QR Code Section - shown above builds list for Android tab */}
      {activeTab === 'android' && (
        <AndroidQRSection latestBuild={currentBuilds.find(b => b.is_latest)} />
      )}

      {/* iOS TestFlight Section */}
      {activeTab === 'ios' ? (
        <>
          <TestFlightSection isSuperAdmin={isSuperAdmin} />
          {isSuperAdmin && <IOSBuildInstructionsSection />}
        </>
      ) : (
        /* Builds List */
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          {isLoading ? (
            <div className="p-8 text-center text-gray-500">
              <Loader2 className="w-8 h-8 mx-auto mb-4 animate-spin text-gray-400" />
              <p>Loading builds...</p>
            </div>
          ) : error ? (
            <div className="p-8 text-center text-red-500">
              <AlertCircle className="w-8 h-8 mx-auto mb-4" />
              <p>Failed to load builds</p>
            </div>
          ) : currentBuilds.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <Package className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>{t('admin.devBuilds.noBuilds', 'No {label} builds available').replace('{label}', BUILD_TYPE_CONFIG[activeTab].label)}</p>
              {isSuperAdmin && (
                <button
                  onClick={() => setShowUploadModal(true)}
                  className="mt-4 text-primary-600 hover:text-primary-700 font-medium"
                >
                  Upload the first build
                </button>
              )}
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {currentBuilds.map((build) => (
                <BuildRow
                  key={build.id}
                  build={build}
                  buildType={activeTab}
                  isSuperAdmin={isSuperAdmin}
                  onDownload={() => downloadMutation.mutate(build.id)}
                  onDelete={() => setDeleteConfirm(build.id)}
                  isDownloading={downloadMutation.isPending && downloadMutation.variables === build.id}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* API Keys Section (Super Admin Only) */}
      {isSuperAdmin && (
        <div className="mt-8 space-y-6">
          <ApiKeysSection />
          <CLICommandsSection />
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <UploadBuildModal
          defaultBuildType={activeTab}
          onClose={() => setShowUploadModal(false)}
          onSuccess={() => {
            setShowUploadModal(false);
            queryClient.invalidateQueries({ queryKey: ['admin-dev-builds'] });
          }}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete Build</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete this build? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => deleteMutation.mutate(deleteConfirm)}
                disabled={deleteMutation.isPending}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {deleteMutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

interface BuildRowProps {
  build: DevBuild;
  buildType: BuildType;
  isSuperAdmin: boolean;
  onDownload: () => void;
  onDelete: () => void;
  isDownloading: boolean;
}

function BuildRow({
  build,
  buildType,
  isSuperAdmin,
  onDownload,
  onDelete,
  isDownloading,
}: BuildRowProps) {
  const config = BUILD_TYPE_CONFIG[buildType];

  return (
    <div className="p-4 sm:p-6 hover:bg-gray-50 transition-colors">
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
        {/* Build info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 mb-2">
            <span className={`px-2.5 py-1 rounded-full text-sm font-medium ${config.color}`}>
              v{build.version}
            </span>
          </div>

          <h3 className="font-medium text-gray-900 truncate">{build.file_name}</h3>

          {build.release_notes && (
            <div className="mt-2 text-sm text-gray-600 line-clamp-2">
              <FileText className="w-4 h-4 inline-block mr-1 text-gray-400" />
              {build.release_notes}
            </div>
          )}

          <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
            <span className="flex items-center gap-1">
              <HardDrive className="w-3.5 h-3.5" />
              {formatBytes(build.file_size)}
            </span>
            <span className="flex items-center gap-1">
              <Calendar className="w-3.5 h-3.5" />
              {formatDate(build.created_at)}
            </span>
            <span className="flex items-center gap-1">
              <User className="w-3.5 h-3.5" />
              {build.uploader_email || 'Unknown'}
            </span>
            <span className="flex items-center gap-1">
              <Download className="w-3.5 h-3.5" />
              {build.download_count} downloads
            </span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 sm:flex-shrink-0">
          <button
            onClick={onDownload}
            disabled={isDownloading}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
          >
            {isDownloading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Download className="w-4 h-4" />
            )}
            Download
          </button>

          {isSuperAdmin && (
            <button
              onClick={onDelete}
              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              title="Delete build"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

interface UploadBuildModalProps {
  defaultBuildType: BuildType;
  onClose: () => void;
  onSuccess: () => void;
}

function UploadBuildModal({ defaultBuildType, onClose, onSuccess }: UploadBuildModalProps) {
  // If iOS is selected, default to Chrome extension instead (iOS uses TestFlight)
  const [buildType, setBuildType] = useState<BuildType>(
    defaultBuildType === 'ios' ? 'extension-chrome' : defaultBuildType
  );
  const [version, setVersion] = useState('');
  const [releaseNotes, setReleaseNotes] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isDetectingVersion, setIsDetectingVersion] = useState(false);
  const [detectedVersion, setDetectedVersion] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Upload progress state
  const [uploadProgress, setUploadProgress] = useState<{
    isUploading: boolean;
    currentChunk: number;
    totalChunks: number;
    bytesUploaded: number;
    totalBytes: number;
    status: string;
  } | null>(null);

  const config = BUILD_TYPE_CONFIG[buildType];

  // Custom upload function with progress tracking
  const uploadWithProgress = async () => {
    if (!file) throw new Error('No file selected');

    const API_URL = import.meta.env.VITE_API_URL || 'https://api.rumio.world';
    const LARGE_FILE_THRESHOLD = 50 * 1024 * 1024; // 50MB
    const CHUNK_SIZE = 50 * 1024 * 1024; // 50MB chunks

    const accessToken = localStorage.getItem('rumio_access_token');
    if (!accessToken) throw new Error('Not authenticated');

    // For small files, use simple upload
    if (file.size <= LARGE_FILE_THRESHOLD) {
      setUploadProgress({
        isUploading: true,
        currentChunk: 1,
        totalChunks: 1,
        bytesUploaded: 0,
        totalBytes: file.size,
        status: 'Uploading...',
      });

      const formData = new FormData();
      formData.append('file', file);
      formData.append('version', version);
      formData.append('build_type', buildType);
      if (releaseNotes) formData.append('release_notes', releaseNotes);
      formData.append('set_as_latest', 'true');

      const response = await fetch(`${API_URL}/api/admin/builds/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${accessToken}` },
        body: formData,
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({ error: 'Upload failed' }));
        throw new Error(err.error || 'Upload failed');
      }

      setUploadProgress(prev => prev ? { ...prev, bytesUploaded: file.size, status: 'Complete!' } : null);
      return response.json();
    }

    // For large files, use multipart upload
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);

    setUploadProgress({
      isUploading: true,
      currentChunk: 0,
      totalChunks,
      bytesUploaded: 0,
      totalBytes: file.size,
      status: 'Initializing upload...',
    });

    // Step 1: Initialize multipart upload
    const initResponse = await fetch(`${API_URL}/api/admin/builds/multipart/init`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        fileName: file.name,
        fileSize: file.size,
        buildType,
        version,
        releaseNotes: releaseNotes || '',
        setAsLatest: true,
      }),
    });

    if (!initResponse.ok) {
      const err = await initResponse.json().catch(() => ({ error: 'Failed to initialize upload' }));
      throw new Error(err.error || 'Failed to initialize upload');
    }

    const { uploadId, fileKey, metadata } = await initResponse.json();

    try {
      // Step 2: Upload parts
      const parts: Array<{ partNumber: number; etag: string }> = [];

      for (let partNumber = 1; partNumber <= totalChunks; partNumber++) {
        const start = (partNumber - 1) * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, file.size);
        const chunk = file.slice(start, end);

        setUploadProgress({
          isUploading: true,
          currentChunk: partNumber,
          totalChunks,
          bytesUploaded: start,
          totalBytes: file.size,
          status: `Uploading chunk ${partNumber} of ${totalChunks}...`,
        });

        const partResponse = await fetch(`${API_URL}/api/admin/builds/multipart/part`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/octet-stream',
            'X-Upload-Id': uploadId,
            'X-File-Key': fileKey,
            'X-Part-Number': partNumber.toString(),
          },
          body: chunk,
        });

        if (!partResponse.ok) {
          throw new Error(`Failed to upload chunk ${partNumber}`);
        }

        const partResult = await partResponse.json();
        parts.push({ partNumber: partResult.partNumber, etag: partResult.etag });

        setUploadProgress({
          isUploading: true,
          currentChunk: partNumber,
          totalChunks,
          bytesUploaded: end,
          totalBytes: file.size,
          status: partNumber === totalChunks ? 'Finalizing...' : `Uploaded chunk ${partNumber} of ${totalChunks}`,
        });
      }

      // Step 3: Complete multipart upload
      setUploadProgress(prev => prev ? { ...prev, status: 'Finalizing upload...' } : null);

      const completeResponse = await fetch(`${API_URL}/api/admin/builds/multipart/complete`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ uploadId, fileKey, parts, metadata }),
      });

      if (!completeResponse.ok) {
        const err = await completeResponse.json().catch(() => ({ error: 'Failed to complete upload' }));
        throw new Error(err.error || 'Failed to complete upload');
      }

      setUploadProgress(prev => prev ? { ...prev, status: 'Complete!' } : null);
      return completeResponse.json();
    } catch (error) {
      // Abort on failure
      await fetch(`${API_URL}/api/admin/builds/multipart/abort`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ uploadId, fileKey }),
      }).catch(() => {});
      throw error;
    }
  };

  const uploadMutation = useMutation({
    mutationFn: uploadWithProgress,
    onSuccess: () => {
      onSuccess();
    },
    onError: (error: Error) => {
      setUploadProgress(null);
      setError(error.message);
    },
  });

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      // Validate file extension
      const ext = selectedFile.name.substring(selectedFile.name.lastIndexOf('.')).toLowerCase();
      const expectedExt = config.accept;
      if (ext !== expectedExt) {
        setError(`Invalid file type. Please select a ${expectedExt} file.`);
        setFile(null);
        return;
      }
      setFile(selectedFile);
      setError(null);

      // Auto-detect version from file
      setIsDetectingVersion(true);
      setDetectedVersion(null);
      try {
        const extracted = await extractVersionFromFile(selectedFile);
        if (extracted) {
          setVersion(extracted.version);
          setDetectedVersion(extracted.version);
        }
      } catch (err) {
        console.warn('Could not auto-detect version:', err);
      } finally {
        setIsDetectingVersion(false);
      }
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!file) {
      setError('Please select a file');
      return;
    }

    if (!version.trim()) {
      setError('Please enter a version number');
      return;
    }

    uploadMutation.mutate();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Upload Build</h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Build Type Selection */}
          <div>
            <span className="block text-sm font-medium text-gray-700 mb-2">Build Type</span>
            <div className="space-y-3">
              {/* Browser Extensions */}
              <div>
                <p className="text-xs text-gray-500 mb-1.5">Browser Extensions</p>
                <div className="flex flex-wrap gap-2">
                  {(['extension-chrome', 'extension-firefox', 'extension-safari'] as BuildType[])
                    .filter((type) => !BUILD_TYPE_CONFIG[type].disabled)
                    .map((type) => {
                      const c = BUILD_TYPE_CONFIG[type];
                      return (
                        <button
                          key={type}
                          type="button"
                          onClick={() => {
                            setBuildType(type);
                            setFile(null);
                            if (fileInputRef.current) {
                              fileInputRef.current.value = '';
                            }
                          }}
                          className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                            buildType === type
                              ? 'bg-primary-100 text-primary-700 ring-2 ring-primary-500'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          <c.icon className="w-4 h-4" />
                          {c.shortLabel}
                        </button>
                      );
                    })}
                </div>
              </div>
              {/* Mobile Apps (iOS uses TestFlight, so only Android is uploadable) */}
              <div>
                <p className="text-xs text-gray-500 mb-1.5">Mobile Apps</p>
                <div className="flex flex-wrap gap-2">
                  {(['android'] as BuildType[])
                    .filter((type) => !BUILD_TYPE_CONFIG[type].disabled)
                    .map((type) => {
                      const c = BUILD_TYPE_CONFIG[type];
                      return (
                        <button
                          key={type}
                          type="button"
                          onClick={() => {
                            setBuildType(type);
                            setFile(null);
                            if (fileInputRef.current) {
                              fileInputRef.current.value = '';
                            }
                          }}
                          className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                            buildType === type
                              ? 'bg-primary-100 text-primary-700 ring-2 ring-primary-500'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          <c.icon className="w-4 h-4" />
                          {c.shortLabel}
                        </button>
                      );
                    })}
                  {/* iOS note */}
                  <span className="flex items-center gap-2 px-3 py-2 text-sm text-gray-400">
                    <Apple className="w-4 h-4" />
                    iOS (via TestFlight)
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* File Upload */}
          <div>
            <span className="block text-sm font-medium text-gray-700 mb-2">
              File ({config.accept})
            </span>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className={`w-full border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                file
                  ? 'border-primary-300 bg-primary-50'
                  : 'border-gray-300 hover:border-gray-400'
              }`}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept={config.accept}
                onChange={handleFileSelect}
                className="hidden"
              />
              {file ? (
                <div className="flex items-center justify-center gap-3">
                  <Check className="w-5 h-5 text-primary-600" />
                  <div className="text-left">
                    <p className="font-medium text-gray-900">{file.name}</p>
                    <p className="text-sm text-gray-500">{formatBytes(file.size)}</p>
                  </div>
                </div>
              ) : (
                <>
                  <Upload className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                  <p className="text-sm text-gray-600">
                    Click to select or drag and drop
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    {config.label} ({config.accept})
                  </p>
                </>
              )}
            </button>
          </div>

          {/* Version */}
          <div>
            <label htmlFor="build-version" className="block text-sm font-medium text-gray-700 mb-2">
              Version
              {isDetectingVersion && (
                <span className="ml-2 text-xs text-gray-400">
                  <Loader2 className="w-3 h-3 inline animate-spin mr-1" />
                  Detecting...
                </span>
              )}
              {detectedVersion && !isDetectingVersion && (
                <span className="ml-2 text-xs text-green-600">
                  <Check className="w-3 h-3 inline mr-1" />
                  Auto-detected from file
                </span>
              )}
            </label>
            <input
              id="build-version"
              type="text"
              value={version}
              onChange={(e) => {
                setVersion(e.target.value);
                if (e.target.value !== detectedVersion) {
                  setDetectedVersion(null);
                }
              }}
              placeholder="e.g., 1.0.0-beta.1"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          {/* Release Notes */}
          <div>
            <label htmlFor="release-notes" className="block text-sm font-medium text-gray-700 mb-2">
              Release Notes (optional)
            </label>
            <textarea
              id="release-notes"
              value={releaseNotes}
              onChange={(e) => setReleaseNotes(e.target.value)}
              placeholder="What's new in this version?"
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
            />
          </div>

          {/* Error */}
          {error && (
            <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 p-3 rounded-lg">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}

          {/* Upload Progress */}
          {uploadProgress && (
            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium text-gray-700">{uploadProgress.status}</span>
                <span className="text-gray-500">
                  {formatBytes(uploadProgress.bytesUploaded)} / {formatBytes(uploadProgress.totalBytes)}
                </span>
              </div>

              {/* Progress bar */}
              <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                <div
                  className="bg-primary-600 h-3 rounded-full transition-all duration-300 ease-out"
                  style={{ width: `${Math.round((uploadProgress.bytesUploaded / uploadProgress.totalBytes) * 100)}%` }}
                />
              </div>

              <div className="flex items-center justify-between text-xs text-gray-500">
                <span>
                  {uploadProgress.totalChunks > 1
                    ? `Chunk ${uploadProgress.currentChunk} of ${uploadProgress.totalChunks}`
                    : 'Uploading...'
                  }
                </span>
                <span className="font-medium">
                  {Math.round((uploadProgress.bytesUploaded / uploadProgress.totalBytes) * 100)}%
                </span>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              disabled={uploadMutation.isPending}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={uploadMutation.isPending || !file || !version.trim()}
              className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {uploadMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4" />
                  Upload
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
